import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate, Outlet } from "react-router-dom";
import { path } from './paths';
import PermissionDenied from "../components/permission-denied";
import { Loader } from "../components";
import useToastify from "../hooks/use-toastify";
import AuthService from "../services/auth-service";
import { useAuth } from "../context/auth/auth-context";
// import withPermission from "./withPermission";

const WallOfFameAndSpotlightListPage = lazy(() => import('../pages/wall-of-fame-and-spotlight/views/wall-of-fame-and-spotlight-list-view'));
const WallOfFameAndSpotlightListPageCreatePage = lazy(() => import('../pages/wall-of-fame-and-spotlight/views/wall-of-fame-and-spotlight-create-view'));
const WallOfFameAndSpotlightDetailPage = lazy(() => import('../pages/wall-of-fame-and-spotlight/views/wall-of-fame-and-spotlight-detail-view'));
const MainLayoutPage = lazy(() => import('../components/main-layout'));
const ManageRecognitionForPage = lazy(() => import('../pages/settings/recognition-for'));
const GeneralSettingsPage = lazy(() => import('../pages/settings/general-config'));

const NoMatchPage = lazy(() => import('../components/no-match'));

export default function HostRouter() {
  const { errorToast } = useToastify();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  const [isLoading, setLoading] = useState(true);
  const [userRole, seUserRole] = useState<any>({});

  const handleMetaData = async (data: any, valueIsLabel: boolean) => {
    const options = data && data.map(function (item: string, index: number) {
      return { value: valueIsLabel ? item : index, label: item.charAt(0).toUpperCase() + item.slice(1) };
    });
    return options
  }

  const handleTeamMetaData = async (data: any) => {
    const options = data && data.map(function (item: any, index: number) {
      return { value: item?.id, label: (item.team).charAt(0).toUpperCase() + item.team.slice(1) };
    });
    return options
  }

  const getMetaData = async () => {
    try {
      const { meta, user, token, permissions, menu }: any = await AuthService.getAuthUser();
      const recognition_for = await handleMetaData(meta.recognition_for, true);
      const nomination_status = await handleMetaData(meta.nomination_status, true);
      const endorsement_status = await handleMetaData(meta.endorsement_status, false);
      const nomination_types = await handleMetaData(meta.nomination_types, false);
      const teams = await handleTeamMetaData(meta.teams);
      setAuth({
        user: user,
        meta: { recognition_for, nomination_status, endorsement_status, nomination_types, teams },
        token: token,
        permissions: permissions,
        menu: menu,
        general_settings: meta.general_config
      });
      seUserRole(user);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Failed to fetch meta data:', error);
    }
  };

  const initialize = useCallback(async () => {
    try {

      const setToken = search.split('=')?.[1];
      const getToken = localStorage.getItem("token");

      if (setToken) {
        decodeURIComponent(setToken)
        localStorage.setItem("token", decodeURIComponent(setToken));
        const searchParams = new URLSearchParams(search);
        if (searchParams.has("token")) {
          searchParams.delete("token");
          const newSearch = searchParams.toString();
          const newUrl = `${pathname}${newSearch ? `?${newSearch}` : ""}`;
          navigate(newUrl, { replace: true }); // Replace history to avoid creating a new history entry
        }
      }

      if (getToken || setToken) {
        await getMetaData();
      } else {
        setLoading(false);
        localStorage.removeItem('token');
        window.location.href = process.env.REACT_APP_CHECK_IN_URL || '';
      }
    } catch (error: any) {
      if (error?.error?.code === 401) {
        localStorage.removeItem('token');
        setLoading(false);
        window.location.href = process.env.REACT_APP_CHECK_IN_URL || '';
      }
      else {
        errorToast(error?.error?.message || 'Something went wrong', { position: "top-right" });
      }
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initialize();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const Protected = useCallback(({ checkRoles, children }: { checkRoles: any, children: JSX.Element }) => {
    const token = localStorage.getItem("token");
    // if (!token) {
    //   // window.location.href = process.env.REACT_APP__URL || '';
    //   return <Navigate to={process.env.REACT_APP__URL||''} />;
    // }
    if (userRole && token && checkRoles) {
      return <>{children}<Outlet /></>
    }
    else {
      return <Navigate to="/permission-denied" />;
    }
  }, [userRole]);


  if (isLoading) {
    return <Loader />;
  }

  return (
    <Suspense fallback={<div><Loader /></div>}>
      <Routes>
        <Route path="/" element={<MainLayoutPage />}>
          {/* Home Route */}
          <Route index element={<Protected checkRoles={userRole.user_role_id === 2 || userRole?.user_role_id === 0 || userRole?.user_role_id === 1}><WallOfFameAndSpotlightListPage /></Protected>} />

          {/* Wall of Fame & Spotlight */}
          <Route path={path.wallOfFameAndSpotlight.list} element={<Protected checkRoles={userRole.user_role_id === 2 || userRole?.user_role_id === 0 || userRole?.user_role_id === 1}><WallOfFameAndSpotlightListPage /></Protected>} />
          <Route path={path.wallOfFameAndSpotlight.create} element={<Protected checkRoles={(userRole.user_role_id === 2 || userRole?.user_role_id === 0) || (userRole?.wall_of_fame_allowed === 1 || userRole?.spotlight_allowed === 1)}><WallOfFameAndSpotlightListPageCreatePage /></Protected>} />
          <Route path={path.wallOfFameAndSpotlight.detail} element={<Protected checkRoles={userRole.user_role_id === 2 || userRole?.user_role_id === 0 || userRole?.wall_of_fame_allowed === 1 || userRole?.spotlight_allowed === 1 || userRole?.endorsement_allowed === 1}><WallOfFameAndSpotlightDetailPage /></Protected>} />
          {/* Settings */}
          <Route path={path.settings.manageRecognitionFor} element={<Protected checkRoles={userRole.user_role_id === 2 || userRole?.user_role_id === 0}><ManageRecognitionForPage /></Protected>} />
          <Route path={path.settings.generalSettings} element={<Protected checkRoles={userRole.user_role_id === 2 || userRole?.user_role_id === 0}><GeneralSettingsPage /></Protected>} />

        </Route>
        {/* Permission Denied */}
        <Route path={path.permissionDenied} element={<PermissionDenied />} />
        {/* No Match */}
        <Route path="*" element={<NoMatchPage />} />
      </Routes>
    </Suspense>
  );
}